import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { apiFetch } from "../lib/api-client";
import { LoadingIndicator } from "./LoadingIndicator";

type DictionaryEntry = {
  id: string;
  term: string;
  reading: string;
};

const DICTIONARY_QUERY_KEY = ["dictionary"];

const fetchEntries = () => apiFetch<DictionaryEntry[]>("/dictionary");

const createEntry = (params: { term: string; reading: string }) =>
  apiFetch<DictionaryEntry>("/dictionary", {
    method: "POST",
    body: JSON.stringify(params),
  });

const deleteEntry = (id: string) =>
  apiFetch<{ id: string }>(`/dictionary/${id}`, { method: "DELETE" });

export function DictionaryEditor() {
  const queryClient = useQueryClient();
  const [term, setTerm] = useState("");
  const [reading, setReading] = useState("");

  const entriesQuery = useQuery({
    queryKey: DICTIONARY_QUERY_KEY,
    queryFn: fetchEntries,
  });

  const createMutation = useMutation({
    mutationFn: createEntry,
    onSuccess: () => {
      setTerm("");
      setReading("");
      queryClient.invalidateQueries({ queryKey: DICTIONARY_QUERY_KEY });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEntry,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: DICTIONARY_QUERY_KEY }),
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!term.trim() || !reading.trim()) return;
    createMutation.mutate({ term: term.trim(), reading: reading.trim() });
  };

  return (
    <section className="space-y-3">
      <div>
        <h2 className="font-semibold">辞書（固有名詞）</h2>
        <p className="text-sm text-neutral-600 mt-1">
          家族の名前や保育園名など、書き起こしで正しく読ませたい語句を登録します。家族で共有されます。
        </p>
      </div>

      {entriesQuery.isLoading && <LoadingIndicator label="読み込み中..." />}

      {entriesQuery.data && entriesQuery.data.length > 0 && (
        <ul className="space-y-2">
          {entriesQuery.data.map((entry) => (
            <li
              key={entry.id}
              className="flex items-center justify-between gap-3 rounded-xl border border-neutral-200 bg-white p-3"
            >
              <div className="text-sm">
                <span className="font-semibold">{entry.term}</span>
                <span className="text-neutral-400 mx-2">→</span>
                <span className="text-neutral-600">{entry.reading}</span>
              </div>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(entry.id)}
                disabled={deleteMutation.isPending}
                className="text-sm text-neutral-400 hover:text-red-600 disabled:opacity-40 transition-colors"
                aria-label={`${entry.term}を削除`}
              >
                削除
              </button>
            </li>
          ))}
        </ul>
      )}

      {entriesQuery.data && entriesQuery.data.length === 0 && (
        <p className="text-sm text-neutral-400">まだ登録されていません</p>
      )}

      <form onSubmit={onSubmit} className="flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[8rem] text-sm">
          単語
          <input
            type="text"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="例: 陽翔"
            className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2"
          />
        </label>
        <label className="flex-1 min-w-[8rem] text-sm">
          よみ（ひらがな）
          <input
            type="text"
            value={reading}
            onChange={(e) => setReading(e.target.value)}
            placeholder="例: はると"
            className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2"
          />
        </label>
        <button
          type="submit"
          disabled={createMutation.isPending || !term.trim() || !reading.trim()}
          className="rounded-lg bg-neutral-900 px-4 py-2 text-white font-semibold disabled:opacity-40 hover:bg-neutral-700 transition-colors"
        >
          追加
        </button>
      </form>

      {createMutation.isError && (
        <p className="text-sm text-red-600">{createMutation.error.message}</p>
      )}
    </section>
  );
}
